export const PARTNER_KINDS = ['INDIVIDUAL', 'COMPANY'];
export const PARTNER_STATUSES = ['PENDING', 'APPROVED', 'SUSPENDED', 'CLOSED'];
export const PARTNER_USER_ROLES = ['OWNER', 'MANAGER', 'STAFF', 'CAR_OWNER'];
/**
 * The roles a partner's team is made of, and so the ones a partner may hand
 * out. `CAR_OWNER` is not one of them: it is an individual the first-party
 * account lists cars for, reached on WhatsApp and never signed in, and only
 * the admin creates one.
 */
export const PARTNER_TEAM_ROLES = ['OWNER', 'MANAGER', 'STAFF'];
/**
 * The address a car owner is filed under when the admin gave none.
 *
 * `partner_users.email` is the identity and cannot be null, but a car owner
 * has no login to identify, so this stands in. `.invalid` is reserved
 * (RFC 2606): nothing is ever delivered there, and no Google or Apple
 * account can carry it, so sign-in can never match one by email.
 */
export const CAR_OWNER_EMAIL_DOMAIN = 'owners.zuruflex.invalid';
export function isPlaceholderEmail(email) {
    return Boolean(email?.toLowerCase().endsWith(`@${CAR_OWNER_EMAIL_DOMAIN}`));
}
export const PAYOUT_METHODS = ['MOBILE_MONEY', 'BANK_TRANSFER'];
export const PARTNER_DOCUMENT_KINDS = [
    'ID_CARD',
    'PROOF_OF_ADDRESS',
    'BUSINESS_REGISTRATION',
    'VEHICLE_REGISTRATION',
    'INSURANCE',
    'ROADWORTHY',
];
export const DOCUMENT_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
/**
 * Documents a company must file that an individual need not, and the other
 * way round. The onboarding checklist and the approval gate both read this,
 * so the two can never drift apart.
 */
export const REQUIRED_PARTNER_DOCUMENTS = {
    INDIVIDUAL: ['ID_CARD'],
    COMPANY: ['BUSINESS_REGISTRATION'],
};
/**
 * Per car, whoever owns it. An uninsured rental is not a rental.
 *
 * Not reviewed one by one: they are part of the car's listing, and the
 * listing review that publishes the car approves them with it.
 */
export const REQUIRED_VEHICLE_DOCUMENTS = [
    'VEHICLE_REGISTRATION',
    'INSURANCE',
    'ROADWORTHY',
];
/** The kinds that belong to one car rather than to the partner as a whole. */
export function isVehicleDocument(kind) {
    return REQUIRED_VEHICLE_DOCUMENTS.includes(kind);
}
// ── Verification ─────────────────────────────────────────────
//
// Everything below is pure. The API derives `Partner.kycStatus` with it and
// both portals render their checklist from it, so the gate that stops an
// unverified partner being approved and the list that tells them why are
// the same twelve lines of arithmetic. They cannot disagree.
/**
 * How one required document looks to the checklist. Wider than
 * `DocumentStatus`, because "never uploaded" and "approved in March and
 * expired in June" are not states a row can hold but are exactly the two a
 * partner most needs to be told about.
 */
export const DOCUMENT_REQUIREMENT_STATES = [
    'MISSING',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'EXPIRED',
];
export function time(value) {
    return value instanceof Date ? value.getTime() : new Date(value).getTime();
}
/**
 * The newest upload of each kind.
 *
 * A re-upload is a new row rather than an overwrite, so a rejection and the
 * corrected document that answered it both survive — but only the latest of
 * each kind is what the partner stands on today.
 */
export function latestDocumentByKind(documents) {
    const latest = new Map();
    for (const document of documents) {
        const held = latest.get(document.kind);
        if (!held || time(document.createdAt) > time(held.createdAt)) {
            latest.set(document.kind, document);
        }
    }
    return latest;
}
/**
 * An approved document that has since lapsed is not an approved document.
 * Treating it as one is how a car ends up on hire on last year's insurance.
 */
export function documentRequirementState(document, now = new Date()) {
    if (!document)
        return 'MISSING';
    if (document.status === 'REJECTED')
        return 'REJECTED';
    if (document.status === 'PENDING')
        return 'PENDING';
    if (document.expiresAt && time(document.expiresAt) <= now.getTime())
        return 'EXPIRED';
    return 'APPROVED';
}
/** What this partner owes Zuruflex, and where each one has got to. */
export function partnerDocumentRequirements(kind, documents, now = new Date()) {
    // Vehicle paperwork is a car's business, not the partner's, and mixing
    // the two would hold up an account over a roadworthy certificate.
    const partnerScoped = documents.filter((document) => !document.vehicleId);
    const latest = latestDocumentByKind(partnerScoped);
    return REQUIRED_PARTNER_DOCUMENTS[kind].map((required) => {
        const document = latest.get(required) ?? null;
        return { kind: required, state: documentRequirementState(document, now), document };
    });
}
/** The same, for one car. */
export function vehicleDocumentRequirements(documents, vehicleId, now = new Date()) {
    const forCar = documents.filter((document) => document.vehicleId === vehicleId);
    const latest = latestDocumentByKind(forCar);
    return REQUIRED_VEHICLE_DOCUMENTS.map((required) => {
        const document = latest.get(required) ?? null;
        return { kind: required, state: documentRequirementState(document, now), document };
    });
}
/**
 * Whether a car's paperwork can go in front of a reviewer.
 *
 * Pending is fine — judging it is what review is for. Missing, lapsed or
 * sent back is not: there is nothing on file to approve.
 */
export function vehiclePaperworkReady(requirements) {
    return requirements.every((requirement) => requirement.state === 'PENDING' || requirement.state === 'APPROVED');
}
/**
 * `Partner.kycStatus`, computed rather than typed in.
 *
 * VERIFIED is the narrow case — every required document present, approved
 * and unexpired — because it is the one that opens the gate. An expired
 * certificate lands in PENDING rather than REJECTED: nobody did anything
 * wrong, a new copy is simply owed.
 */
export function deriveKycStatus(kind, documents, now = new Date()) {
    const states = partnerDocumentRequirements(kind, documents, now).map((each) => each.state);
    if (states.length === 0)
        return 'VERIFIED';
    if (states.every((state) => state === 'APPROVED'))
        return 'VERIFIED';
    if (states.some((state) => state === 'REJECTED'))
        return 'REJECTED';
    if (states.every((state) => state === 'MISSING'))
        return 'UNVERIFIED';
    return 'PENDING';
}
// ── The onboarding checklist ─────────────────────────────────
/**
 * Which version of Zuruflex's partner agreement is in force.
 *
 * The number lives here rather than beside the prose in `@zuruflex/core`
 * because it is the part the rest of the model compares against, and
 * `@zuruflex/types` is the one package everything may import. The wording is
 * `PARTNER_TERMS` in `@zuruflex/core/legal`, and it reads its version from
 * this constant so the two cannot drift.
 *
 * **Raising it re-gates every partner**: the portal shows the agreement
 * again on their next sign-in and `canSubmit` goes false until an owner
 * accepts it. That is the intended way to ship a change to the terms, and
 * the reason to think before bumping it for a typo.
 */
export const PARTNER_AGREEMENT_VERSION = 1;
export const ONBOARDING_STEPS = ['AGREEMENT', 'PROFILE', 'DOCUMENTS', 'PAYOUT', 'VEHICLE'];
/** The particulars Zuruflex needs on file before it can put a name to a partner. */
export function isProfileComplete(partner) {
    if (partner.kind === 'COMPANY') {
        return Boolean(partner.legalName?.trim() && partner.registrationNumber?.trim());
    }
    return Boolean(partner.contactEmail?.trim());
}
export function isPayoutComplete(payout) {
    return Boolean(payout.payoutMethod && payout.payoutAccountNumber?.trim() && payout.payoutAccountName?.trim());
}
/**
 * Whether this partner has accepted the agreement in force.
 *
 * A stored version lower than the current one is somebody who agreed to an
 * earlier draft, which is why this compares rather than reading a boolean:
 * "has agreed" is not a fact about a partner, it is a fact about a partner
 * and a version.
 */
export function hasAcceptedAgreement(partner) {
    return (partner.platformTermsVersion ?? 0) >= (partner.agreementVersion ?? PARTNER_AGREEMENT_VERSION);
}
/** Which steps block submission. */
const BLOCKING_STEPS = ['AGREEMENT', 'PROFILE', 'DOCUMENTS'];
export function buildOnboarding(input) {
    const { partner, payout, documents, vehicleCount } = input;
    const now = input.now ?? new Date();
    const requirements = partnerDocumentRequirements(partner.kind, documents, now);
    const kycStatus = deriveKycStatus(partner.kind, documents, now);
    const done = {
        AGREEMENT: hasAcceptedAgreement(partner),
        PROFILE: isProfileComplete(partner),
        DOCUMENTS: kycStatus === 'VERIFIED',
        PAYOUT: isPayoutComplete(payout),
        VEHICLE: vehicleCount > 0,
    };
    const steps = ONBOARDING_STEPS.map((step) => ({
        step,
        done: done[step],
        required: BLOCKING_STEPS.includes(step),
    }));
    const blocking = steps.filter((step) => step.required);
    const settled = blocking.filter((step) => step.done);
    return {
        status: partner.status,
        kycStatus,
        submittedAt: partner.submittedAt ?? null,
        steps,
        documents: requirements,
        // Re-submitting an account already approved, or already in the queue,
        // would do nothing but move it to the back of that queue.
        canSubmit: blocking.every((step) => step.done) && partner.status === 'PENDING' && !partner.submittedAt,
        progress: blocking.length === 0 ? 1 : settled.length / blocking.length,
    };
}
